// src/Pages/ValmikiLeadForm.jsx
// VALMIKI AI LAB — Lead generation POPUP (full form).
// Opens automatically 5 seconds after the page loads. Closes with the × button, the Escape key,
// or a click on the dark backdrop — and then re-opens 5 seconds after closing, so the popup
// keeps coming back until the visitor submits the form.
// ONE self-contained file: form + validation + submit logic + all CSS. Render <ValmikiLeadForm /> once
// per page (e.g. at the bottom of ValmikiHome). No navbar/footer here — it is an overlay, not a page.
//
// Any button can also open it:  window.dispatchEvent(new Event("valmiki:open-lead"))

import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

/* ───────────────────────── CONFIG ───────────────────────── */

const OPEN_AFTER_MS = 5000;    // open 5 s after page load
const REOPEN_AFTER_MS = 5000;  // re-open 5 s after the user closes it
const SESSION_KEY = "valmiki_lead_popup_done"; // set only after a successful submit

// Where leads are POSTed as JSON. Leave "" while developing: the lead is logged to the console
// and the success state still shows.
// Works with: your own API, Google Apps Script web-app URL, Zapier/Make webhook, Formspree, etc.
const LEAD_ENDPOINT = "";

// Optional WhatsApp number (country code, no +). Leave "" to hide the WhatsApp link.
const WHATSAPP_NUMBER = "";

/* ───────────────────────── CONTENT ───────────────────────── */

const audiences = [
  { value: "student", label: "Student" },
  { value: "college", label: "College / institution" },
  { value: "parent", label: "Parent" },
  { value: "professional", label: "Working professional" },
];

const interests = [
  "AI Career Development Program",
  "7-day AI career & placement bootcamp",
  "VALMIKI AI LAB (experimentation space)",
  "College partnership",
  "Not sure yet — I need guidance",
];

const callTimes = ["Morning (9 am – 12 pm)", "Afternoon (12 – 4 pm)", "Evening (4 – 8 pm)", "Anytime"];

const nextSteps = [
  { t: "You send this form", d: "It takes about a minute. Only name, phone and email are required." },
  { t: "We call you within 1 working day", d: "A programme counsellor explains the format, fees and batch dates." },
  { t: "You decide", d: "No pressure. If it is not the right fit, we will say so." },
];

const INITIAL = {
  audience: "student",
  name: "",
  email: "",
  phone: "",
  city: "",
  org: "",
  detail: "",
  interest: interests[0],
  callTime: callTimes[3],
  message: "",
  consent: false,
  website: "", // honeypot — real users never fill this
};

const audienceFields = {
  student: { org: "College / school", orgPh: "e.g. Jadavpur University", detail: "Course & year", detailPh: "e.g. B.Tech CSE, 3rd year" },
  college: { org: "Institution name", orgPh: "e.g. ABC Institute of Technology", detail: "Your designation", detailPh: "e.g. Principal, Placement Head" },
  parent: { org: "Child’s college / school", orgPh: "Optional", detail: "Child’s course & year", detailPh: "Optional" },
  professional: { org: "Company", orgPh: "e.g. Acme Pvt. Ltd.", detail: "Current role", detailPh: "e.g. Marketing Executive" },
};

/* ───────────────────────── STYLES (in-page) ───────────────────────── */

const css = `
@import url('https://fonts.googleapis.com/css2?family=IBM+Plex+Mono:wght@400;500&family=IBM+Plex+Sans:wght@400;500;600&family=Newsreader:ital,opsz,wght@0,6..72,400;0,6..72,500;0,6..72,600;1,6..72,400&display=swap');

.vlf-overlay{
  --ink:#0B1626; --ink-2:#132238; --paper:#F3F5F8; --white:#FFFFFF;
  --gold:#C8A24A; --gold-hi:#E0BC62; --gold-deep:#7D5F12;
  --slate:#4F5C73; --on-ink:#F3F5F8; --on-ink-mute:rgba(243,245,248,.74);
  --line:rgba(11,22,38,.16); --line-ink:rgba(243,245,248,.16); --err:#B3261E;
  --serif:'Newsreader',Georgia,'Times New Roman',serif;
  --sans:'IBM Plex Sans','Segoe UI',Arial,sans-serif;
  --mono:'IBM Plex Mono',ui-monospace,Menlo,Consolas,monospace;
  position:fixed;inset:0;z-index:1000;display:flex;align-items:center;justify-content:center;
  padding:16px;background:rgba(8,17,30,.72);backdrop-filter:blur(3px);-webkit-backdrop-filter:blur(3px);
  font-family:var(--sans);color:var(--ink);line-height:1.55;-webkit-font-smoothing:antialiased;
}
.vlf-overlay *{box-sizing:border-box;}
.vlf-overlay :where(h1,h2,h3,p,ul,ol,fieldset,legend){margin:0;padding:0;}
.vlf-overlay :where(ul,ol){list-style:none;}
.vlf-overlay :focus-visible{outline:2px solid var(--gold-deep);outline-offset:3px;}

/* ── SINGLE SCROLLBAR: the modal is the only scroll container ── */
.vlf-modal{position:relative;display:grid;grid-template-columns:.75fr 1.25fr;width:100%;max-width:760px;
  max-height:72vh;background:var(--white);border-radius:10px;
  overflow-y:auto;overflow-x:hidden;
  box-shadow:0 30px 80px rgba(0,0,0,.5);
  scrollbar-width:thin;scrollbar-color:rgba(11,22,38,.35) transparent;}
/* subtle custom scrollbar for the modal (WebKit) */
.vlf-modal::-webkit-scrollbar{width:10px;}
.vlf-modal::-webkit-scrollbar-track{background:transparent;}
.vlf-modal::-webkit-scrollbar-thumb{background:rgba(11,22,38,.28);border-radius:10px;border:2px solid transparent;background-clip:content-box;}
.vlf-modal::-webkit-scrollbar-thumb:hover{background:rgba(11,22,38,.5);background-clip:content-box;}

/* ── columns are NOT independent scroll containers anymore ── */
.vlf-pitch{background-color:var(--ink);color:var(--on-ink);padding:22px 20px;
  background-image:linear-gradient(rgba(243,245,248,.035) 1px,transparent 1px),
                   linear-gradient(90deg,rgba(243,245,248,.035) 1px,transparent 1px);
  background-size:56px 56px;}
.vlf-h1{font-family:var(--serif);font-weight:500;font-size:1.25rem;line-height:1.15;letter-spacing:-.015em;margin-bottom:10px;}
.vlf-lead{font-size:12.5px;line-height:1.5;color:var(--on-ink-mute);margin-bottom:14px;}
.vlf-steps-title{font:600 12px/1.4 var(--sans);color:var(--gold);margin-bottom:4px;}
.vlf-steps{counter-reset:s;}
.vlf-steps li{position:relative;padding:10px 0 10px 34px;border-bottom:1px solid var(--line-ink);}
.vlf-steps li:first-child{border-top:1px solid var(--line-ink);}
.vlf-steps li::before{counter-increment:s;content:counter(s);position:absolute;left:0;top:10px;
  width:22px;height:22px;border-radius:50%;border:1.5px solid var(--gold);color:var(--gold-hi);
  display:flex;align-items:center;justify-content:center;font:500 11px/1 var(--mono);}
.vlf-steps strong{display:block;font-family:var(--serif);font-weight:500;font-size:0.9375rem;line-height:1.25;margin-bottom:2px;}
.vlf-steps span{font-size:11.5px;color:var(--on-ink-mute);}
.vlf-alt{margin-top:12px;font-size:11.5px;color:var(--on-ink-mute);}
.vlf-alt a{color:var(--gold-hi);text-underline-offset:3px;}

/* form side: no own scrollbar */
.vlf-formside{padding:20px 22px 16px;}

.vlf-close{position:absolute;top:10px;right:10px;z-index:2;width:32px;height:32px;border-radius:50%;
  border:1.5px solid var(--ink);background-color:var(--white);color:var(--ink);cursor:pointer;
  background-image:linear-gradient(var(--ink),var(--ink));background-repeat:no-repeat;
  background-origin:border-box;background-position:left center;background-size:0% 100%;
  font:400 18px/1 var(--sans);display:flex;align-items:center;justify-content:center;
  transition:background-size .45s cubic-bezier(.65,0,.35,1),color .18s ease .13s;}
.vlf-close:hover,.vlf-close:focus-visible{background-size:100% 100%;color:var(--paper);}

.vlf-card-title{font-family:var(--serif);font-weight:500;font-size:1.25rem;line-height:1.2;margin-bottom:3px;padding-right:36px;}
.vlf-card-sub{font-size:12.5px;color:var(--slate);margin-bottom:12px;}

.vlf-fieldset{border:0;min-width:0;margin-bottom:11px;}
.vlf-legend{font:600 12.5px/1.4 var(--sans);margin-bottom:5px;padding:0;}
.vlf-chips{display:flex;flex-wrap:wrap;gap:6px;}
.vlf-chip{position:relative;}
.vlf-chip input{position:absolute;opacity:0;inset:0;width:100%;height:100%;margin:0;cursor:pointer;}
.vlf-chip span{display:inline-block;padding:6px 10px;border:1px solid var(--line);border-radius:2px;
  font-size:12px;font-weight:500;color:var(--ink);background:var(--white);
  transition:background-color .2s,color .2s,border-color .2s;}
.vlf-chip input:hover + span{border-color:var(--ink);}
.vlf-chip input:checked + span{background:var(--ink);color:var(--paper);border-color:var(--ink);}
.vlf-chip input:focus-visible + span{outline:2px solid var(--gold-deep);outline-offset:3px;}

.vlf-row{display:grid;grid-template-columns:1fr 1fr;gap:14px;}
.vlf-field{margin-bottom:9px;display:flex;flex-direction:column;}
.vlf-field label{font-size:12.5px;font-weight:600;margin-bottom:3px;}
.vlf-opt{font-weight:400;color:var(--slate);}
.vlf-input,.vlf-select,.vlf-textarea{
  font:400 14px/1.4 var(--sans);color:var(--ink);background:var(--paper);
  border:1px solid var(--line);border-radius:2px;padding:8px 10px;width:100%;
  transition:border-color .15s,background-color .15s;}
.vlf-textarea{min-height:52px;resize:vertical;}
.vlf-input::placeholder,.vlf-textarea::placeholder{color:#8A94A6;}
.vlf-input:hover,.vlf-select:hover,.vlf-textarea:hover{border-color:rgba(11,22,38,.4);}
.vlf-input:focus,.vlf-select:focus,.vlf-textarea:focus{outline:0;border-color:var(--gold-deep);background:var(--white);
  box-shadow:0 0 0 3px rgba(200,162,74,.28);}
.vlf-field.has-err .vlf-input,.vlf-field.has-err .vlf-select{border-color:var(--err);}
.vlf-err{font-size:11.5px;color:var(--err);margin-top:3px;}

.vlf-consent{display:flex;gap:8px;align-items:flex-start;margin:2px 0 11px;font-size:11.5px;color:var(--slate);line-height:1.4;}
.vlf-consent input{flex:none;width:16px;height:16px;margin-top:1px;accent-color:var(--ink);}
.vlf-consent.has-err{color:var(--err);}
.vlf-hp{position:absolute;left:-9999px;width:1px;height:1px;overflow:hidden;}
.vlf-banner{margin-bottom:10px;padding:7px 10px;border-left:3px solid var(--err);background:#FBEDEC;font-size:12.5px;color:var(--err);}

/* buttons — two lines orbit the border, background wipes on hover (same as the rest of the site) */
@property --vlf-a{syntax:"<angle>";inherits:false;initial-value:0deg;}
.vlf-btn{
  --fill:#162B47; --edge:var(--gold); --line-c:var(--gold); --wipe:var(--gold); --hover-text:var(--ink); --text:var(--on-ink);
  position:relative;overflow:hidden;isolation:isolate;width:100%;
  display:inline-flex;align-items:center;justify-content:center;padding:11px 20px;border-radius:2px;
  font:600 14px/1 var(--sans);cursor:pointer;color:var(--text);border:1px solid var(--edge);
  background-color:var(--fill);background-image:linear-gradient(var(--wipe),var(--wipe));
  background-repeat:no-repeat;background-origin:border-box;background-position:left center;background-size:0% 100%;
  transition:background-size .45s cubic-bezier(.65,0,.35,1),color .18s ease .13s,border-color .3s ease;
}
.vlf-btn::before{content:"";position:absolute;inset:0;padding:2px;border-radius:inherit;pointer-events:none;
  background:conic-gradient(from var(--vlf-a),transparent 0deg,var(--line-c) 55deg,transparent 56deg,transparent 180deg,var(--line-c) 235deg,transparent 236deg);
  -webkit-mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);-webkit-mask-composite:xor;
  mask:linear-gradient(#000 0 0) content-box,linear-gradient(#000 0 0);mask-composite:exclude;
  animation:vlf-orbit 3.2s linear infinite;}
@keyframes vlf-orbit{to{--vlf-a:360deg;}}
.vlf-btn:hover:not(:disabled),.vlf-btn:focus-visible{background-size:100% 100%;color:var(--hover-text);border-color:var(--wipe);}
.vlf-btn:disabled{opacity:.7;cursor:progress;}
.vlf-btn-gold{animation:vlf-breathe 4s ease-in-out infinite;}
@keyframes vlf-breathe{
  0%,100%{background-color:#162B47;box-shadow:0 6px 14px rgba(0,0,0,.25);}
  50%{background-color:#24426B;box-shadow:0 6px 18px rgba(200,162,74,.30);}
}
.vlf-btn-line{--fill:transparent;--edge:var(--ink);--line-c:var(--ink);--wipe:var(--ink);--hover-text:var(--paper);--text:var(--ink);width:auto;}
.vlf-fine{margin-top:7px;font-size:11px;color:var(--slate);text-align:center;}

/* success */
.vlf-done{padding:20px 0 8px;}
.vlf-done-tick{width:42px;height:42px;border-radius:50%;border:1.5px solid var(--gold-deep);
  display:flex;align-items:center;justify-content:center;margin-bottom:16px;}
.vlf-done h2{font-family:var(--serif);font-weight:500;font-size:1.5rem;line-height:1.2;margin-bottom:10px;}
.vlf-done p{color:var(--slate);max-width:44ch;margin-bottom:20px;font-size:14px;}

/* opening: one orchestrated moment */
@media (prefers-reduced-motion:no-preference){
  .vlf-overlay{animation:vlf-fade .25s ease both;}
  .vlf-modal{animation:vlf-pop .35s cubic-bezier(.2,.7,.2,1) both;}
}
@keyframes vlf-fade{from{opacity:0;}to{opacity:1;}}
@keyframes vlf-pop{from{opacity:0;transform:translateY(24px) scale(.97);}to{opacity:1;transform:none;}}
@keyframes vlf-reveal{from{opacity:0;transform:translateY(-4px);}to{opacity:1;transform:none;}}
.vlf-reveal{animation:vlf-reveal .25s ease;}
@media (prefers-reduced-motion:reduce){
  .vlf-btn,.vlf-chip span,.vlf-close{transition:none;}
  .vlf-btn::before,.vlf-btn-gold,.vlf-reveal{animation:none;}
}

/* responsive: pitch panel hides on tablets/phones, form fills the modal */
@media (max-width:820px){
  .vlf-modal{grid-template-columns:1fr;max-width:460px;max-height:80vh;}
  .vlf-pitch{display:none;}
  .vlf-formside{padding:20px 18px 16px;}
}
@media (max-width:480px){
  .vlf-overlay{padding:10px;align-items:flex-end;}
  .vlf-modal{max-height:calc(100vh - 20px);}
  .vlf-row{grid-template-columns:1fr;gap:0;}
}
`;

/* ───────────────────────── HELPERS ───────────────────────── */

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

// Indian mobile (10 digits starting 6–9, optional +91 / 0 prefix) — other countries: +, then 8–15 digits.
const cleanPhone = (raw) => raw.replace(/[\s\-().]/g, "");
const phoneOk = (raw) => {
  const p = cleanPhone(raw);
  return /^(\+91|91|0)?[6-9]\d{9}$/.test(p) || /^\+\d{8,15}$/.test(p);
};

function validate(v) {
  const e = {};
  if (v.name.trim().length < 2) e.name = "Enter your full name.";
  if (!EMAIL_RE.test(v.email.trim())) e.email = "Enter a valid email, like name@example.com.";
  if (!phoneOk(v.phone)) e.phone = "Enter a valid mobile number, e.g. 98300 12345 or +971 50 123 4567.";
  if (!v.consent) e.consent = "Please tick this box so we can contact you.";
  return e;
}

function readTracking() {
  if (typeof window === "undefined") return {};
  const q = new URLSearchParams(window.location.search);
  const t = {};
  ["utm_source", "utm_medium", "utm_campaign", "utm_term", "utm_content"].forEach((k) => {
    if (q.get(k)) t[k] = q.get(k);
  });
  t.page = window.location.pathname;
  t.referrer = document.referrer || "";
  return t;
}

// "Done" only means "submitted" now — closing does NOT mark the session done, so the popup can re-open.
const markDone = () => {
  try { sessionStorage.setItem(SESSION_KEY, "1"); } catch (_) { /* private mode — ignore */ }
};
const wasDone = () => {
  try { return sessionStorage.getItem(SESSION_KEY) === "1"; } catch (_) { return false; }
};

/* ───────────────────────── SMALL PIECES ───────────────────────── */

const Field = ({ id, label, optional, error, children }) => (
  <div className={`vlf-field${error ? " has-err" : ""}`}>
    <label htmlFor={id}>
      {label}
      {optional && <span className="vlf-opt"> (optional)</span>}
    </label>
    {children}
    {error && <p className="vlf-err" id={`${id}-err`} role="alert">{error}</p>}
  </div>
);

/* ───────────────────────── COMPONENT ───────────────────────── */

export default function ValmikiLeadForm() {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(INITIAL);
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState("idle"); // idle | sending | done | failed
  const tracking = useMemo(readTracking, []);
  const modalRef = useRef(null);
  const closeRef = useRef(null);
  const returnFocusTo = useRef(null);
  const reopenTimerRef = useRef(null);

  /* initial open: 5 s after mount, unless the form was already submitted this session */
  useEffect(() => {
    if (wasDone()) return undefined;
    const t = setTimeout(() => setOpen(true), OPEN_AFTER_MS);
    return () => clearTimeout(t);
  }, []);

  /* any button can open it: window.dispatchEvent(new Event("valmiki:open-lead")) */
  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener("valmiki:open-lead", show);
    return () => window.removeEventListener("valmiki:open-lead", show);
  }, []);

  /* clear any pending reopen timer when the component unmounts */
  useEffect(() => () => {
    if (reopenTimerRef.current) clearTimeout(reopenTimerRef.current);
  }, []);

  /* Closing just hides the popup — it will re-open REOPEN_AFTER_MS later, unless the
     visitor already submitted successfully (status === "done"). */
  const close = useCallback(() => {
    setOpen(false);
    if (status !== "done") {
      if (reopenTimerRef.current) clearTimeout(reopenTimerRef.current);
      reopenTimerRef.current = setTimeout(() => setOpen(true), REOPEN_AFTER_MS);
    }
  }, [status]);

  /* while open: lock page scroll, focus the close button, Escape closes, Tab stays inside */
  useEffect(() => {
    if (!open) return undefined;
    returnFocusTo.current = document.activeElement;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    closeRef.current?.focus();

    const onKey = (e) => {
      if (e.key === "Escape") { close(); return; }
      if (e.key !== "Tab" || !modalRef.current) return;
      const nodes = modalRef.current.querySelectorAll(
        'button:not([disabled]),input:not([disabled]):not([tabindex="-1"]),select,textarea,a[href]'
      );
      if (!nodes.length) return;
      const first = nodes[0];
      const last = nodes[nodes.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
      returnFocusTo.current?.focus?.();
    };
  }, [open, close]);

  const af = audienceFields[values.audience];

  const set = (key) => (e) => {
    const val = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setValues((v) => ({ ...v, [key]: val }));
    if (errors[key]) setErrors((er) => ({ ...er, [key]: undefined }));
  };

  async function onSubmit(e) {
    e.preventDefault();
    if (status === "sending") return;

    if (values.website) { // bot filled the hidden field — pretend success, send nothing
      setStatus("done");
      markDone();
      return;
    }

    const found = validate(values);
    setErrors(found);
    if (Object.keys(found).length) {
      const first = ["name", "phone", "email", "consent"].find((k) => found[k]);
      document.getElementById(`vlf-${first}`)?.focus();
      return;
    }

    const { website, consent, ...rest } = values;
    const lead = {
      ...rest,
      name: rest.name.trim(),
      email: rest.email.trim().toLowerCase(),
      phone: cleanPhone(rest.phone),
      consent: true,
      submittedAt: new Date().toISOString(),
      source: "valmiki-lead-popup",
      ...tracking,
    };

    setStatus("sending");
    try {
      if (LEAD_ENDPOINT) {
        const res = await fetch(LEAD_ENDPOINT, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(lead),
        });
        if (!res.ok) throw new Error(`Server responded ${res.status}`);
      } else {
        // eslint-disable-next-line no-console
        console.info("[VALMIKI lead — set LEAD_ENDPOINT to send this]", lead);
        await new Promise((r) => setTimeout(r, 600));
      }
      setStatus("done");
      markDone();
      // Cancel any scheduled reopen — a successful submit means the popup stays closed.
      if (reopenTimerRef.current) { clearTimeout(reopenTimerRef.current); reopenTimerRef.current = null; }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      setStatus("failed");
    }
  }

  if (!open) return null;

  const firstName = values.name.trim().split(/\s+/)[0];
  const waHref = WHATSAPP_NUMBER
    ? `https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent("Hi VALMIKI AI LAB, I would like to know more about the programme.")}`
    : "";

  return (
    <div className="vlf-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <style>{css}</style>

      <div className="vlf-modal" ref={modalRef} role="dialog" aria-modal="true" aria-labelledby="vlf-title">
        <button type="button" className="vlf-close" ref={closeRef} onClick={close} aria-label="Close this window">
          ×
        </button>

        {/* LEFT — what happens after you submit */}
        <div className="vlf-pitch">
          <h2 className="vlf-h1">Talk to a counsellor about the AI Career Development Program.</h2>
          <p className="vlf-lead">
            Share your details and we will call you with the programme format, batch dates and fees.
          </p>
          <p className="vlf-steps-title">What happens next</p>
          <ol className="vlf-steps">
            {nextSteps.map((s) => (
              <li key={s.t}>
                <strong>{s.t}</strong>
                <span>{s.d}</span>
              </li>
            ))}
          </ol>
          {waHref && (
            <p className="vlf-alt">
              Prefer to chat first?{" "}
              <a href={waHref} target="_blank" rel="noopener noreferrer">Message us on WhatsApp</a>
            </p>
          )}
        </div>

        {/* RIGHT — the form */}
        <div className="vlf-formside">
          {status === "done" ? (
            <div className="vlf-done vlf-reveal" role="status" aria-live="polite">
              <div className="vlf-done-tick" aria-hidden="true">
                <svg width="20" height="20" viewBox="0 0 12 12" fill="none">
                  <path d="M2.5 6.4L5 8.9L9.6 3.6" stroke="#7D5F12" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
              </div>
              <h2 id="vlf-title">{firstName ? `Thank you, ${firstName}.` : "Thank you."}</h2>
              <p>
                We have your details. A VALMIKI counsellor will call you within one working day
                {values.callTime !== "Anytime" ? `, around your preferred slot (${values.callTime.toLowerCase()})` : ""}.
              </p>
              <button type="button" className="vlf-btn vlf-btn-line" onClick={close}>Close</button>
            </div>
          ) : (
            <form onSubmit={onSubmit} noValidate>
              <h2 className="vlf-card-title" id="vlf-title">Request a call back</h2>
              <p className="vlf-card-sub">Free, no obligation. Fields marked optional can be skipped.</p>

              <div className="vlf-hp" aria-hidden="true">
                <label htmlFor="vlf-website">Leave this field empty</label>
                <input id="vlf-website" type="text" tabIndex={-1} autoComplete="off" value={values.website} onChange={set("website")} />
              </div>

              <fieldset className="vlf-fieldset">
                <legend className="vlf-legend">I am a</legend>
                <div className="vlf-chips" role="radiogroup">
                  {audiences.map((a) => (
                    <label key={a.value} className="vlf-chip">
                      <input type="radio" name="vlf-audience" value={a.value}
                        checked={values.audience === a.value} onChange={set("audience")} />
                      <span>{a.label}</span>
                    </label>
                  ))}
                </div>
              </fieldset>

              <Field id="vlf-name" label="Full name" error={errors.name}>
                <input id="vlf-name" className="vlf-input" type="text" autoComplete="name" placeholder="e.g. Ananya Sen"
                  value={values.name} onChange={set("name")} aria-invalid={!!errors.name}
                  aria-describedby={errors.name ? "vlf-name-err" : undefined} />
              </Field>

              <div className="vlf-row">
                <Field id="vlf-phone" label="Mobile number" error={errors.phone}>
                  <input id="vlf-phone" className="vlf-input" type="tel" inputMode="tel" autoComplete="tel" placeholder="98300 12345"
                    value={values.phone} onChange={set("phone")} aria-invalid={!!errors.phone}
                    aria-describedby={errors.phone ? "vlf-phone-err" : undefined} />
                </Field>
                <Field id="vlf-email" label="Email" error={errors.email}>
                  <input id="vlf-email" className="vlf-input" type="email" autoComplete="email" placeholder="name@example.com"
                    value={values.email} onChange={set("email")} aria-invalid={!!errors.email}
                    aria-describedby={errors.email ? "vlf-email-err" : undefined} />
                </Field>
              </div>

              <div className="vlf-row vlf-reveal" key={values.audience}>
                <Field id="vlf-org" label={af.org} optional>
                  <input id="vlf-org" className="vlf-input" type="text" autoComplete="organization" placeholder={af.orgPh}
                    value={values.org} onChange={set("org")} />
                </Field>
                <Field id="vlf-detail" label={af.detail} optional>
                  <input id="vlf-detail" className="vlf-input" type="text" placeholder={af.detailPh}
                    value={values.detail} onChange={set("detail")} />
                </Field>
              </div>

              <div className="vlf-row">
                <Field id="vlf-city" label="City" optional>
                  <input id="vlf-city" className="vlf-input" type="text" autoComplete="address-level2" placeholder="e.g. Kolkata"
                    value={values.city} onChange={set("city")} />
                </Field>
                <Field id="vlf-calltime" label="Best time to call">
                  <select id="vlf-calltime" className="vlf-select" value={values.callTime} onChange={set("callTime")}>
                    {callTimes.map((t) => <option key={t}>{t}</option>)}
                  </select>
                </Field>
              </div>

              <Field id="vlf-interest" label="I am interested in">
                <select id="vlf-interest" className="vlf-select" value={values.interest} onChange={set("interest")}>
                  {interests.map((t) => <option key={t}>{t}</option>)}
                </select>
              </Field>

              <Field id="vlf-message" label="Anything you would like us to know" optional>
                <textarea id="vlf-message" className="vlf-textarea" placeholder="Questions, preferred batch, current skills…"
                  value={values.message} onChange={set("message")} />
              </Field>

              <label className={`vlf-consent${errors.consent ? " has-err" : ""}`}>
                <input id="vlf-consent" type="checkbox" checked={values.consent} onChange={set("consent")} aria-invalid={!!errors.consent} />
                <span>
                  I agree to be contacted by VALMIKI AI LAB by phone, WhatsApp or email about this enquiry.
                  {errors.consent && <><br /><strong role="alert">{errors.consent}</strong></>}
                </span>
              </label>

              {status === "failed" && (
                <p className="vlf-banner" role="alert">
                  We could not send your details. Check your connection and press the button again.
                </p>
              )}

              <button type="submit" className="vlf-btn vlf-btn-gold" disabled={status === "sending"}>
                {status === "sending" ? "Sending…" : "Request a call back"}
              </button>
              <p className="vlf-fine">We use your details only to respond to this enquiry.</p>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}